import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useContentRefresh } from "../lib/content";
import { Trash2, Edit2, Plus, LogOut, Save, X, Inbox, RotateCcw, ExternalLink, Image as ImageIcon, Upload, Lock, Eye, EyeOff } from "lucide-react";

const EMPTY = {
  date: "",
  title: "",
  venue: "",
  city: "",
  country: "NZ",
  year: new Date().getFullYear(),
  is_upcoming: false,
  ticket_url: "",
  poster_url: "",
  gallery_url: "",
  notes: "",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("ip_admin_token") || "");
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("content");

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/admin/login", { password });
      localStorage.setItem("ip_admin_token", res.data.token);
      setToken(res.data.token);
      toast.success("Signed in");
    } catch {
      toast.error("Invalid password");
    }
  };

  const logout = () => {
    localStorage.removeItem("ip_admin_token");
    setToken("");
  };

  if (!token) {
    return (
      <div className="px-6 md:px-12 py-20" data-testid="admin-login-page">
        <div className="max-w-md mx-auto hairline p-10">
          <h1 className="font-title text-2xl tracking-[0.1em] text-silver-100 mb-8">
            / ADMIN
          </h1>
          <form onSubmit={login} className="flex flex-col gap-4" data-testid="admin-login-form">
            <label>
              <span className="ip-label">Password</span>
              <input
                type="password"
                className="ip-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                data-testid="admin-password"
              />
            </label>
            <button type="submit" className="btn-sharp mt-4" data-testid="admin-login-submit">
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 py-14" data-testid="admin-dashboard">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex items-center justify-between mb-10">
          <h1 className="font-title text-3xl tracking-[0.1em] text-silver-100">/ ADMIN</h1>
          <button onClick={logout} className="btn-ghost" data-testid="admin-logout">
            <LogOut size={14} /> SIGN OUT
          </button>
        </div>

        <TechRiderPasswordCard />

        <div className="flex gap-2 hairline-b mb-10 flex-wrap" data-testid="admin-tabs">
          {[
            { k: "content", label: "Content" },
            { k: "events", label: "Events" },
            { k: "sets", label: "Sets" },
            { k: "videos", label: "Videos" },
            { k: "releases", label: "Releases" },
            { k: "merch", label: "Merch" },
            { k: "reviews", label: "Reviews" },
            { k: "submissions", label: "Inbox" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`font-title text-[0.65rem] tracking-[0.26em] uppercase px-4 py-3 ${
                tab === t.k
                  ? "text-silver-100 border-b border-silver-100"
                  : "text-silver-500 hover:text-silver-200"
              }`}
              data-testid={`admin-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "content" && <ContentAdmin />}
        {tab === "events" && <EventsAdmin />}
        {tab === "sets" && <SetsAdmin />}
        {tab === "videos" && <VideosAdmin />}
        {tab === "releases" && <ReleasesAdmin />}
        {tab === "merch" && <MerchAdmin />}
        {tab === "reviews" && <ReviewsAdmin />}
        {tab === "submissions" && <SubmissionsAdmin />}
      </div>
    </div>
  );
}

// ---------- REVIEWS ADMIN ----------
function ReviewsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending | approved | all

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/reviews");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const approve = async (id) => {
    try {
      await api.put(`/admin/reviews/${id}/approve`);
      toast.success("Approved — now public");
      load();
    } catch {
      toast.error("Error");
    }
  };
  const unapprove = async (id) => {
    try {
      await api.put(`/admin/reviews/${id}/unapprove`);
      toast.success("Unapproved");
      load();
    } catch {
      toast.error("Error");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Error");
    }
  };

  const filtered = items.filter((r) => {
    if (filter === "pending") return !r.approved;
    if (filter === "approved") return r.approved;
    return true;
  });
  const pendingCount = items.filter((r) => !r.approved).length;

  return (
    <div data-testid="reviews-admin">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div className="font-title text-[0.65rem] tracking-[0.3em] text-silver-400">
          {items.length} TOTAL · {pendingCount} PENDING
        </div>
        <div className="flex gap-2">
          {[
            { k: "pending", label: "Pending" },
            { k: "approved", label: "Approved" },
            { k: "all", label: "All" },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`font-title text-[0.6rem] tracking-[0.24em] uppercase px-3 py-1.5 border ${
                filter === f.k
                  ? "border-silver-200 text-silver-100 bg-silver-900/60"
                  : "border-silver-800 text-silver-500 hover:text-silver-200"
              }`}
              data-testid={`admin-review-filter-${f.k}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="text-silver-500 text-sm">Loading…</p>}

      <div className="flex flex-col gap-4" data-testid="admin-review-list">
        {filtered.map((r) => (
          <div key={r.id} className="hairline p-6" data-testid={`admin-review-${r.id}`}>
            <div className="flex justify-between items-start mb-3 flex-wrap gap-3">
              <div>
                <span
                  className={`font-title text-[0.55rem] tracking-[0.24em] px-2 py-0.5 border ${
                    r.approved
                      ? "border-silver-200 text-silver-100"
                      : "border-silver-700 text-silver-500"
                  }`}
                >
                  {r.approved ? "APPROVED" : "PENDING"}
                </span>
                <h3 className="font-title text-lg tracking-[0.1em] text-silver-100 mt-2">
                  {r.name}
                </h3>
                <a
                  href={`mailto:${r.email}`}
                  className="font-body text-xs text-silver-400 hover:text-silver-100"
                >
                  {r.email}
                </a>
              </div>
              <span className="font-body text-[0.65rem] text-silver-600">
                {new Date(r.created_at).toLocaleString()}
              </span>
            </div>
            <p className="font-body text-silver-300 text-sm leading-relaxed whitespace-pre-wrap mb-4">
              "{r.message}"
            </p>
            <div className="flex gap-3 flex-wrap">
              {!r.approved ? (
                <button
                  onClick={() => approve(r.id)}
                  className="btn-sharp"
                  data-testid={`admin-review-approve-${r.id}`}
                >
                  Approve
                </button>
              ) : (
                <button
                  onClick={() => unapprove(r.id)}
                  className="btn-ghost"
                  data-testid={`admin-review-unapprove-${r.id}`}
                >
                  Unapprove
                </button>
              )}
              <button
                onClick={() => remove(r.id)}
                className="btn-ghost text-red-400/80 hover:text-red-400"
                data-testid={`admin-review-delete-${r.id}`}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {!loading && filtered.length === 0 && (
          <p className="font-body text-silver-500 text-sm py-10 text-center">
            No reviews in this filter.
          </p>
        )}
      </div>
    </div>
  );
}

// ---------- RELEASES ADMIN ----------
const EMPTY_RELEASE = {
  title: "",
  kind: "track",
  label: "",
  release_date: "",
  image_url: "",
  link_url: "",
  notes: "",
};

function ReleasesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_RELEASE);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const r = await api.get("/releases");
    setItems(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const startEdit = (r) => {
    setEditing(r.id);
    setForm({ ...EMPTY_RELEASE, ...r });
  };
  const startNew = () => {
    setEditing("new");
    setForm(EMPTY_RELEASE);
  };
  const cancel = () => {
    setEditing(null);
    setForm(EMPTY_RELEASE);
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.title) {
      toast.error("Title is required");
      return;
    }
    setLoading(true);
    try {
      if (editing === "new") {
        await api.post("/releases", form);
        toast.success("Release created");
      } else {
        await api.put(`/releases/${editing}`, form);
        toast.success("Release updated");
      }
      await load();
      cancel();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete release?")) return;
    try {
      await api.delete(`/releases/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="releases-admin">
      <div className="flex justify-between items-center mb-6">
        <div className="font-title text-[0.65rem] tracking-[0.3em] text-silver-400">
          {items.length} RELEASES
        </div>
        <button onClick={startNew} className="btn-sharp" data-testid="admin-new-release">
          <Plus size={14} /> New Release
        </button>
      </div>

      {editing && (
        <div className="hairline p-6 mb-10 grid md:grid-cols-3 gap-4" data-testid="admin-release-editor">
          <div className="md:col-span-2">
            <Field label="Title *">
              <input className="ip-input" value={form.title} onChange={upd("title")} data-testid="rel-title" />
            </Field>
          </div>
          <Field label="Kind">
            <select
              className="ip-input bg-black"
              value={form.kind}
              onChange={upd("kind")}
              data-testid="rel-kind"
            >
              <option value="track">Track</option>
              <option value="va">VA</option>
              <option value="remix">Remix</option>
              <option value="ep">EP</option>
              <option value="album">Album</option>
            </select>
          </Field>
          <Field label="Label">
            <input className="ip-input" value={form.label} onChange={upd("label")} data-testid="rel-label" />
          </Field>
          <Field label="Release Date">
            <input type="date" className="ip-input" value={form.release_date} onChange={upd("release_date")} data-testid="rel-date" />
          </Field>
          <div className="md:col-span-3">
            <Field label="Cover image">
              <input className="ip-input" value={form.image_url} onChange={upd("image_url")} data-testid="rel-image" placeholder="Paste URL or upload below" />
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                {form.image_url && (
                  <img src={form.image_url} alt="cover" className="h-16 w-16 object-cover border border-silver-800" onError={(e) => (e.currentTarget.style.display = "none")} />
                )}
                <UploadImageButton
                  onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))}
                  slot={`release-cover-${editing || "new"}`}
                  accept="image"
                />
              </div>
            </Field>
          </div>
          <div className="md:col-span-3">
            <Field label="Link URL (Spotify / Bandcamp / SoundCloud)">
              <input className="ip-input" value={form.link_url} onChange={upd("link_url")} data-testid="rel-link" />
            </Field>
          </div>
          <div className="md:col-span-3">
            <Field label="Notes">
              <input className="ip-input" value={form.notes} onChange={upd("notes")} data-testid="rel-notes" />
            </Field>
          </div>
          <div className="md:col-span-3 flex gap-3 justify-end pt-4">
            <button onClick={cancel} className="btn-ghost" data-testid="rel-cancel">
              <X size={14} /> Cancel
            </button>
            <button onClick={save} className="btn-sharp" disabled={loading} data-testid="rel-save">
              <Save size={14} /> {loading ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col" data-testid="admin-release-list">
        {items.map((r) => (
          <div
            key={r.id}
            className="hairline-t py-3 grid grid-cols-12 gap-3 items-center text-sm"
            data-testid={`admin-release-${r.id}`}
          >
            <div className="col-span-2 font-title text-[0.62rem] tracking-[0.2em] text-silver-500 uppercase">
              {r.kind}
            </div>
            <div className="col-span-4 text-silver-200 truncate">{r.title}</div>
            <div className="col-span-3 text-silver-500 text-xs truncate">{r.label || "—"}</div>
            <div className="col-span-2 text-silver-500 text-xs">{r.release_date || "TBA"}</div>
            <div className="col-span-1 flex justify-end gap-2">
              <button onClick={() => startEdit(r)} className="p-1.5 text-silver-400 hover:text-silver-100" data-testid={`admin-edit-release-${r.id}`}>
                <Edit2 size={14} />
              </button>
              <button onClick={() => remove(r.id)} className="p-1.5 text-silver-400 hover:text-red-400" data-testid={`admin-delete-release-${r.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-silver-500 text-sm py-10 text-center">No releases yet.</p>
        )}
      </div>
    </div>
  );
}

function EventsAdmin() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null); // event id or "new"
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const r = await api.get("/events");
    setEvents(r.data);
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = (ev) => {
    setEditing(ev.id);
    setForm({
      ...EMPTY,
      ...ev,
      year: ev.year || new Date(ev.date).getFullYear() || new Date().getFullYear(),
    });
  };
  const startNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };
  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.date || !form.title || !form.venue) {
      toast.error("Date, title and venue are required");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, year: parseInt(form.year) || new Date(form.date).getFullYear() };
      if (editing === "new") {
        await api.post("/events", payload);
        toast.success("Event created");
      } else {
        await api.put(`/events/${editing}`, payload);
        toast.success("Event updated");
      }
      await load();
      cancel();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const upd = (k) => (e) =>
    setForm({
      ...form,
      [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value,
    });

  return (
    <div data-testid="events-admin">
      <div className="flex justify-between items-center mb-6">
        <div className="font-title text-[0.65rem] tracking-[0.3em] text-silver-400">
          {events.length} TOTAL
        </div>
        <button onClick={startNew} className="btn-sharp" data-testid="admin-new-event">
          <Plus size={14} /> New Event
        </button>
      </div>

      {editing && (
        <div className="hairline p-6 mb-10 grid md:grid-cols-3 gap-4" data-testid="admin-event-editor">
          <Field label="Date (YYYY-MM-DD) *">
            <input type="date" className="ip-input" value={form.date} onChange={upd("date")} data-testid="edit-date" />
          </Field>
          <Field label="Year">
            <input type="number" className="ip-input" value={form.year} onChange={upd("year")} data-testid="edit-year" />
          </Field>
          <Field label="Country">
            <input className="ip-input" value={form.country} onChange={upd("country")} data-testid="edit-country" />
          </Field>
          <div className="md:col-span-3">
            <Field label="Title *">
              <input className="ip-input" value={form.title} onChange={upd("title")} data-testid="edit-title" />
            </Field>
          </div>
          <Field label="Venue *">
            <input className="ip-input" value={form.venue} onChange={upd("venue")} data-testid="edit-venue" />
          </Field>
          <Field label="City">
            <input className="ip-input" value={form.city} onChange={upd("city")} data-testid="edit-city" />
          </Field>
          <div className="flex items-end gap-3 pb-2">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-upcoming-label">
              <input
                type="checkbox"
                checked={form.is_upcoming}
                onChange={upd("is_upcoming")}
                className="accent-silver-200"
                data-testid="edit-upcoming"
              />
              <span className="font-title text-[0.6rem] tracking-[0.24em] text-silver-300 uppercase">
                Is Upcoming
              </span>
            </label>
          </div>
          <Field label="Ticket URL">
            <input className="ip-input" value={form.ticket_url} onChange={upd("ticket_url")} data-testid="edit-ticket-url" />
          </Field>
          <Field label="Poster (image)">
            <input className="ip-input" value={form.poster_url} onChange={upd("poster_url")} data-testid="edit-poster-url" placeholder="Paste URL or upload below" />
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              {form.poster_url && (
                <img src={form.poster_url} alt="poster" className="h-14 w-14 object-cover border border-silver-800" onError={(e) => (e.currentTarget.style.display = "none")} />
              )}
              <UploadImageButton
                onUploaded={(url) => setForm((f) => ({ ...f, poster_url: url }))}
                slot={`event-poster-${editing || "new"}`}
                accept="image"
              />
            </div>
          </Field>
          <Field label="Gallery URL (link to media album / Drive / Instagram post)">
            <input className="ip-input" value={form.gallery_url} onChange={upd("gallery_url")} data-testid="edit-gallery-url" placeholder="https://..." />
          </Field>
          <div className="md:col-span-3">
            <Field label="Notes">
              <input className="ip-input" value={form.notes} onChange={upd("notes")} data-testid="edit-notes" />
            </Field>
          </div>
          <div className="md:col-span-3 flex gap-3 justify-end pt-4">
            <button onClick={cancel} className="btn-ghost" data-testid="edit-cancel">
              <X size={14} /> Cancel
            </button>
            <button onClick={save} className="btn-sharp" disabled={loading} data-testid="edit-save">
              <Save size={14} /> {loading ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col" data-testid="admin-event-list">
        {events.map((ev) => (
          <div
            key={ev.id}
            className="hairline-t py-3 grid grid-cols-12 gap-3 items-center text-sm"
            data-testid={`admin-event-${ev.id}`}
          >
            <div className="col-span-2 font-title text-[0.65rem] tracking-[0.2em] text-silver-200">
              {ev.date}
            </div>
            <div className="col-span-5 text-silver-300 truncate">{ev.title}</div>
            <div className="col-span-3 text-silver-500 text-xs truncate">
              {ev.venue} · {ev.city || ev.country}
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              {ev.is_upcoming && (
                <span className="font-title text-[0.55rem] tracking-[0.2em] text-silver-100 border border-silver-300 px-2 py-0.5">
                  UP
                </span>
              )}
              <button onClick={() => startEdit(ev)} className="p-1.5 hover:text-silver-100 text-silver-400" data-testid={`admin-edit-${ev.id}`}>
                <Edit2 size={14} />
              </button>
              <button onClick={() => remove(ev.id)} className="p-1.5 hover:text-red-400 text-silver-400" data-testid={`admin-delete-${ev.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SubmissionsAdmin() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/submissions")
      .then((r) => setSubs(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-silver-500 text-sm">Loading…</p>;
  if (subs.length === 0)
    return (
      <div className="flex flex-col items-center gap-4 py-20" data-testid="subs-empty">
        <Inbox size={28} className="text-silver-600" />
        <p className="font-title text-[0.65rem] tracking-[0.3em] text-silver-500">NO MESSAGES YET</p>
      </div>
    );

  return (
    <div className="flex flex-col gap-4" data-testid="submissions-list">
      {subs.map((s) => (
        <div key={s.id} className="hairline p-6" data-testid={`sub-${s.id}`}>
          <div className="flex justify-between items-start mb-3">
            <div>
              <span className="font-title text-[0.62rem] tracking-[0.3em] text-silver-400 uppercase">
                / {s.kind}
              </span>
              <h3 className="font-title text-lg tracking-[0.1em] text-silver-100 mt-1">
                {s.promoter_name}
              </h3>
              <a
                href={`mailto:${s.email}`}
                className="font-body text-xs text-silver-400 hover:text-silver-100"
              >
                {s.email}
              </a>
            </div>
            <span className="font-body text-[0.65rem] text-silver-600">
              {new Date(s.created_at).toLocaleString()}
            </span>
          </div>
          {(s.event_name || s.venue || s.city || s.budget) && (
            <div className="grid md:grid-cols-4 gap-3 my-3 font-body text-xs text-silver-400">
              {s.event_name && (
                <div>
                  <span className="text-silver-600">Event: </span>{s.event_name}
                </div>
              )}
              {s.venue && (
                <div>
                  <span className="text-silver-600">Venue: </span>{s.venue}
                </div>
              )}
              {s.city && (
                <div>
                  <span className="text-silver-600">City: </span>{s.city}, {s.country}
                </div>
              )}
              {s.budget && (
                <div>
                  <span className="text-silver-600">Budget: </span>{s.budget}
                </div>
              )}
            </div>
          )}
          <p className="font-body text-silver-300 text-sm leading-relaxed whitespace-pre-wrap">
            {s.message}
          </p>
        </div>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="ip-label">{label}</span>
      {children}
    </label>
  );
}

// ---------- UPLOAD IMAGE BUTTON ----------
function UploadImageButton({ onUploaded, slot, accept = "image" }) {
  const inputId = `upload-${slot}`;
  const [uploading, setUploading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // accept: "image" | "video" | "media" (both) | "pdf"
  const acceptAttr =
    accept === "image"
      ? "image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif"
      : accept === "video"
      ? "video/mp4,video/quicktime,video/webm"
      : accept === "pdf"
      ? "application/pdf"
      : "image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif,video/mp4,video/quicktime,video/webm";

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB — max 50 MB)`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    const isHeic = /\.(heic|heif)$/i.test(file.name);
    if (isHeic) toast.info("Converting HEIC → JPEG…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Store the URL as-is (relative /api/uploads/<file>) so it works
      // from any host (preview, production, custom domain).
      const url = res.data.url;
      onUploaded(url, res.data);
      toast.success("Uploaded — press Save to apply");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        detail ||
        (err.response?.status ? `Upload failed (HTTP ${err.response.status})` : `Upload failed: ${err.message}`);
      toast.error(msg);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const label =
    accept === "image" ? "Upload image" : accept === "video" ? "Upload video" : accept === "pdf" ? "Upload PDF" : "Upload image / video";

  return (
    <>
      <input
        id={inputId}
        type="file"
        accept={acceptAttr}
        className="hidden"
        onChange={handle}
        data-testid={`upload-file-${slot}`}
      />
      <label
        htmlFor={inputId}
        className={`btn-ghost cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}
        data-testid={`upload-btn-${slot}`}
      >
        <Upload size={12} /> {uploading ? "Uploading…" : label}
      </label>
    </>
  );
}

// ---------- CONTENT ADMIN (CMS) ----------
function ContentAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // slot -> new value
  const [savingSlot, setSavingSlot] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [query, setQuery] = useState("");
  const refreshSite = useContentRefresh();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/content");
      setItems(r.data);
      // Default: first group open
      if (r.data.length && Object.keys(openGroups).length === 0) {
        const groups = [...new Set(r.data.map((b) => b.group))];
        setOpenGroups(Object.fromEntries(groups.map((g, i) => [g, i === 0])));
      }
    } catch (e) {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? items.filter(
          (b) =>
            b.slot.toLowerCase().includes(q) ||
            b.label.toLowerCase().includes(q) ||
            (b.value || "").toLowerCase().includes(q)
        )
      : items;
    const g = {};
    filtered.forEach((b) => {
      g[b.group] = g[b.group] || [];
      g[b.group].push(b);
    });
    // Sort groups alphabetically (the "01 ·", "02 ·"… prefix gives natural page order;
    // "Brand & Social" lands at the end after the 13 numbered groups).
    return Object.fromEntries(
      Object.entries(g).sort(([a], [b]) => a.localeCompare(b))
    );
  }, [items, query]);

  const onChange = (slot, value) => {
    setDrafts((d) => ({ ...d, [slot]: value }));
  };

  const save = async (block) => {
    const value = drafts[block.slot];
    if (value === undefined) return;
    setSavingSlot(block.slot);
    try {
      await api.put(`/content/${block.slot}`, { value });
      setItems((prev) => prev.map((b) => (b.slot === block.slot ? { ...b, value } : b)));
      setDrafts((d) => {
        const n = { ...d };
        delete n[block.slot];
        return n;
      });
      refreshSite();
      toast.success("Saved — visible on the site");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingSlot(null);
    }
  };

  const discard = (slot) => {
    setDrafts((d) => {
      const n = { ...d };
      delete n[slot];
      return n;
    });
  };

  const resetDefaults = async () => {
    if (
      !window.confirm(
        "Re-seed missing content blocks to defaults? Your existing edits are preserved."
      )
    )
      return;
    setResetting(true);
    try {
      await api.post("/content/reset");
      toast.success("Defaults synced");
      await load();
      refreshSite();
    } catch {
      toast.error("Failed");
    } finally {
      setResetting(false);
    }
  };

  const toggleGroup = (g) => setOpenGroups((o) => ({ ...o, [g]: !o[g] }));

  if (loading) return <p className="text-silver-500 text-sm">Loading content…</p>;

  return (
    <div data-testid="content-admin">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div className="font-title text-[0.65rem] tracking-[0.3em] text-silver-400">
          {items.length} EDITABLE BLOCKS · {Object.keys(grouped).length} GROUPS
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            placeholder="Search slot, label or value…"
            className="ip-input w-64"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="content-search"
          />
          <button
            onClick={resetDefaults}
            disabled={resetting}
            className="btn-ghost"
            data-testid="content-reset"
            title="Re-seed any missing default blocks (does not overwrite your edits)"
          >
            <RotateCcw size={14} /> {resetting ? "Syncing…" : "Sync Defaults"}
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3" data-testid="content-groups">
        {Object.keys(grouped).length === 0 && (
          <p className="text-silver-500 text-sm py-10 text-center">No matches.</p>
        )}
        {Object.entries(grouped).map(([group, blocks]) => {
          const open = openGroups[group] ?? true;
          const dirtyCount = blocks.filter((b) => drafts[b.slot] !== undefined).length;
          // Map group prefix to public page path
          const groupToPath = {
            "01 · Home": "/",
            "02 · About": "/about",
            "03 · Sets": "/sets",
            "04 · Videos": "/videos",
            "05 · Events": "/events",
            "06 · Inner Dance": "/inner-dance",
            "07 · Releases": "/releases",
            "08 · Community": "/community",
            "09 · Press Kit": "/press-kit",
            "10 · Booking": "/booking",
            "11 · Tech Rider": "/tech-rider",
            "12 · Reviews": "/reviews",
            "13 · Merch": "/merch",
          };
          const previewPath = groupToPath[group];
          return (
            <div key={group} className="hairline" data-testid={`content-group-${group}`}>
              <div className="w-full flex justify-between items-center px-6 py-4 hover:bg-silver-950/40">
                <button
                  onClick={() => toggleGroup(group)}
                  className="flex items-center gap-4 flex-1 text-left"
                  data-testid={`content-group-toggle-${group}`}
                >
                  <span className="font-title text-[0.72rem] tracking-[0.28em] text-silver-100 uppercase">
                    {group}
                  </span>
                  <span className="font-body text-[0.65rem] text-silver-600">
                    {blocks.length} {blocks.length === 1 ? "block" : "blocks"}
                  </span>
                  {dirtyCount > 0 && (
                    <span className="font-title text-[0.55rem] tracking-[0.22em] px-2 py-0.5 border border-amber-400/60 text-amber-300">
                      {dirtyCount} UNSAVED
                    </span>
                  )}
                </button>
                <div className="flex items-center gap-3">
                  {previewPath && (
                    <a
                      href={previewPath}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-title text-[0.58rem] tracking-[0.26em] uppercase text-silver-400 hover:text-silver-100 transition-colors flex items-center gap-1"
                      data-testid={`content-group-preview-${group}`}
                      title="Open this page in a new tab"
                    >
                      <ExternalLink size={11} /> Preview
                    </a>
                  )}
                  <button
                    onClick={() => toggleGroup(group)}
                    className="text-silver-500 text-xs ml-2"
                    aria-label="Toggle"
                  >
                    {open ? "−" : "+"}
                  </button>
                </div>
              </div>

              {open && (
                <div className="flex flex-col">
                  {blocks.map((b) => {
                    const draft = drafts[b.slot];
                    const dirty = draft !== undefined && draft !== b.value;
                    const current = draft !== undefined ? draft : b.value || "";
                    const isMultiline = b.kind === "text" && (current?.length > 80 || current?.includes("\n"));
                    return (
                      <div
                        key={b.slot}
                        className="hairline-t px-6 py-5 grid md:grid-cols-12 gap-4 items-start"
                        data-testid={`content-block-${b.slot}`}
                      >
                        <div className="md:col-span-4">
                          <div className="font-title text-[0.7rem] tracking-[0.18em] text-silver-200 uppercase">
                            {b.label || b.slot}
                          </div>
                          <div className="font-body text-[0.6rem] text-silver-600 mt-1 break-all flex items-center gap-2">
                            <code>{b.slot}</code>
                            <span
                              className={`px-1.5 py-0.5 border text-[0.55rem] tracking-[0.18em] uppercase ${
                                b.kind === "image"
                                  ? "border-silver-700 text-silver-400"
                                  : b.kind === "url"
                                  ? "border-silver-700 text-silver-400"
                                  : "border-silver-800 text-silver-500"
                              }`}
                            >
                              {b.kind}
                            </span>
                          </div>
                        </div>

                        <div className="md:col-span-6">
                          {isMultiline ? (
                            <textarea
                              className="ip-input w-full resize-y min-h-[110px]"
                              value={current}
                              onChange={(e) => onChange(b.slot, e.target.value)}
                              data-testid={`content-input-${b.slot}`}
                            />
                          ) : (
                            <input
                              className="ip-input w-full"
                              value={current}
                              onChange={(e) => onChange(b.slot, e.target.value)}
                              data-testid={`content-input-${b.slot}`}
                            />
                          )}
                          {(b.kind === "image" || b.kind === "media") && (                            <div className="mt-2 flex items-center gap-3 flex-wrap">
                              {current && /\.(mp4|mov|webm|m4v)(\?|$)/i.test(current) ? (
                                <video
                                  src={current}
                                  muted
                                  loop
                                  playsInline
                                  className="h-20 w-32 object-cover border border-silver-800 bg-black"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              ) : current ? (
                                <img
                                  src={current}
                                  alt="preview"
                                  className="h-14 w-14 object-cover border border-silver-800"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              ) : null}
                              <UploadImageButton
                                onUploaded={(url) => onChange(b.slot, url)}
                                slot={b.slot}
                                accept={b.kind === "media" ? "media" : "image"}
                              />
                              {current && (
                                <a
                                  href={current}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-silver-500 hover:text-silver-200 text-xs flex items-center gap-1"
                                >
                                  <ImageIcon size={12} /> Open
                                </a>
                              )}
                            </div>
                          )}
                          {b.kind === "url" && current && (
                            <a
                              href={current}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex items-center gap-1 text-silver-500 hover:text-silver-200 text-xs"
                            >
                              <ExternalLink size={12} /> Open link
                            </a>
                          )}
                          {b.kind === "pdf" && (
                            <div className="mt-2 flex items-center gap-3 flex-wrap">
                              {current && (
                                <a
                                  href={current.startsWith("http") ? current : `${process.env.REACT_APP_BACKEND_URL}${current}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-title text-[0.6rem] tracking-[0.24em] uppercase text-silver-200 hairline px-3 py-1.5 hover:bg-silver-900/40"
                                >
                                  ↗ View current PDF
                                </a>
                              )}
                              <UploadImageButton
                                onUploaded={(url) => onChange(b.slot, url)}
                                slot={b.slot}
                                accept="pdf"
                              />
                            </div>
                          )}
                        </div>

                        <div className="md:col-span-2 flex md:justify-end gap-2">
                          {dirty && (
                            <>
                              <button
                                onClick={() => discard(b.slot)}
                                className="btn-ghost"
                                data-testid={`content-discard-${b.slot}`}
                              >
                                <X size={12} />
                              </button>
                              <button
                                onClick={() => save(b)}
                                disabled={savingSlot === b.slot}
                                className="btn-sharp"
                                data-testid={`content-save-${b.slot}`}
                              >
                                <Save size={12} /> {savingSlot === b.slot ? "…" : "Save"}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- SETS ADMIN ----------
const EMPTY_SET = {
  title: "",
  genre: "",
  url: "",
  year: "",
  location: "",
  notes: "",
  order: 0,
};

function SetsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SET);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const r = await api.get("/sets");
    setItems(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({ ...EMPTY_SET, ...s });
  };
  const startNew = () => {
    setEditing("new");
    setForm(EMPTY_SET);
  };
  const cancel = () => {
    setEditing(null);
    setForm(EMPTY_SET);
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.title || !form.url) {
      toast.error("Title and URL are required");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, order: parseInt(form.order) || 0 };
      if (editing === "new") {
        await api.post("/sets", payload);
        toast.success("Set created");
      } else {
        await api.put(`/sets/${editing}`, payload);
        toast.success("Set updated");
      }
      await load();
      cancel();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this set?")) return;
    try {
      await api.delete(`/sets/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="sets-admin">
      <div className="flex justify-between items-center mb-6">
        <div className="font-title text-[0.65rem] tracking-[0.3em] text-silver-400">
          {items.length} SETS
        </div>
        <button onClick={startNew} className="btn-sharp" data-testid="admin-new-set">
          <Plus size={14} /> New Set
        </button>
      </div>

      {editing && (
        <div className="hairline p-6 mb-10 grid md:grid-cols-3 gap-4" data-testid="admin-set-editor">
          <div className="md:col-span-2">
            <Field label="Title *">
              <input className="ip-input" value={form.title} onChange={upd("title")} data-testid="set-title" />
            </Field>
          </div>
          <Field label="Order">
            <input type="number" className="ip-input" value={form.order} onChange={upd("order")} data-testid="set-order" />
          </Field>
          <Field label="Genre">
            <input className="ip-input" value={form.genre} onChange={upd("genre")} placeholder="Techno / Hardgroove / …" data-testid="set-genre" />
          </Field>
          <Field label="Year">
            <input className="ip-input" value={form.year} onChange={upd("year")} data-testid="set-year" />
          </Field>
          <Field label="Location">
            <input className="ip-input" value={form.location} onChange={upd("location")} data-testid="set-location" />
          </Field>
          <div className="md:col-span-3">
            <Field label="URL * (SoundCloud / Mixcloud / YouTube)">
              <input className="ip-input" value={form.url} onChange={upd("url")} data-testid="set-url" />
            </Field>
          </div>
          <div className="md:col-span-3">
            <Field label="Notes">
              <input className="ip-input" value={form.notes} onChange={upd("notes")} data-testid="set-notes" />
            </Field>
          </div>
          <div className="md:col-span-3 flex gap-3 justify-end pt-4">
            <button onClick={cancel} className="btn-ghost" data-testid="set-cancel">
              <X size={14} /> Cancel
            </button>
            <button onClick={save} className="btn-sharp" disabled={loading} data-testid="set-save">
              <Save size={14} /> {loading ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col" data-testid="admin-set-list">
        {items.map((s) => (
          <div
            key={s.id}
            className="hairline-t py-3 grid grid-cols-12 gap-3 items-center text-sm"
            data-testid={`admin-set-${s.id}`}
          >
            <div className="col-span-1 font-title text-[0.62rem] tracking-[0.2em] text-silver-500">
              #{s.order || 0}
            </div>
            <div className="col-span-4 text-silver-200 truncate">{s.title}</div>
            <div className="col-span-2 text-silver-500 text-xs truncate">{s.genre || "—"}</div>
            <div className="col-span-2 text-silver-500 text-xs truncate">{s.year || "—"}</div>
            <div className="col-span-2 text-silver-500 text-xs truncate">
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-silver-200 inline-flex items-center gap-1">
                <ExternalLink size={11} /> Listen
              </a>
            </div>
            <div className="col-span-1 flex justify-end gap-2">
              <button onClick={() => startEdit(s)} className="p-1.5 text-silver-400 hover:text-silver-100" data-testid={`admin-edit-set-${s.id}`}>
                <Edit2 size={14} />
              </button>
              <button onClick={() => remove(s.id)} className="p-1.5 text-silver-400 hover:text-red-400" data-testid={`admin-delete-set-${s.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-silver-500 text-sm py-10 text-center">No sets yet.</p>
        )}
      </div>
    </div>
  );
}

// ---------- MERCH ADMIN ----------
const EMPTY_MERCH = {
  category: "fans",
  title: "",
  description: "",
  image_url: "",
  price: "",
  details: "",
  available: true,
  order: 0,
};

function MerchAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_MERCH);
  const [loading, setLoading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    const r = await api.get("/merch");
    setItems(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const startEdit = (m) => {
    setEditing(m.id);
    setForm({ ...EMPTY_MERCH, ...m });
  };
  const startNew = () => {
    setEditing("new");
    setForm(EMPTY_MERCH);
  };
  const cancel = () => {
    setEditing(null);
    setForm(EMPTY_MERCH);
  };
  const upd = (k) => (e) =>
    setForm({
      ...form,
      [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value,
    });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10 MB)");
      e.target.value = "";
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data.url.startsWith("http")
        ? res.data.url
        : `${BACKEND_URL}${res.data.url}`;
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Uploaded — press Save to apply");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.title) {
      toast.error("Title is required");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, order: parseInt(form.order) || 0 };
      if (editing === "new") {
        await api.post("/merch", payload);
        toast.success("Product created");
      } else {
        await api.put(`/merch/${editing}`, payload);
        toast.success("Product updated");
      }
      await load();
      cancel();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/merch/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const byCategory = items.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});
  const CATS = ["fans", "stickers", "ashtrays"];

  return (
    <div data-testid="merch-admin">
      <div className="flex justify-between items-center mb-6">
        <div className="font-title text-[0.65rem] tracking-[0.3em] text-silver-400">
          {items.length} PRODUCTS
        </div>
        <button onClick={startNew} className="btn-sharp" data-testid="admin-new-merch">
          <Plus size={14} /> New Product
        </button>
      </div>

      {editing && (
        <div className="hairline p-6 mb-10 grid md:grid-cols-3 gap-4" data-testid="admin-merch-editor">
          <Field label="Category *">
            <select
              className="ip-input bg-black"
              value={form.category}
              onChange={upd("category")}
              data-testid="merch-category"
            >
              <option value="fans">Fans</option>
              <option value="stickers">Stickers</option>
              <option value="ashtrays">Ashtrays</option>
            </select>
          </Field>
          <div className="md:col-span-2">
            <Field label="Title *">
              <input className="ip-input" value={form.title} onChange={upd("title")} data-testid="merch-title" />
            </Field>
          </div>
          <div className="md:col-span-3">
            <Field label="Description">
              <textarea
                rows={3}
                className="ip-input resize-y"
                value={form.description}
                onChange={upd("description")}
                data-testid="merch-description"
              />
            </Field>
          </div>
          <Field label="Price (free text e.g. €25)">
            <input className="ip-input" value={form.price} onChange={upd("price")} data-testid="merch-price" />
          </Field>
          <Field label="Details (size · material…)">
            <input className="ip-input" value={form.details} onChange={upd("details")} data-testid="merch-details" />
          </Field>
          <Field label="Order">
            <input type="number" className="ip-input" value={form.order} onChange={upd("order")} data-testid="merch-order" />
          </Field>
          <div className="md:col-span-3">
            <Field label="Image URL">
              <input className="ip-input" value={form.image_url} onChange={upd("image_url")} data-testid="merch-image-url" />
            </Field>
            <div className="mt-3 flex items-center gap-4 flex-wrap">
              {form.image_url && (
                <img
                  src={form.image_url}
                  alt="preview"
                  className="h-20 w-20 object-cover border border-silver-800"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              )}
              <input
                id="merch-upload-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handleUpload}
                data-testid="merch-upload-file"
              />
              <label htmlFor="merch-upload-input" className="btn-ghost cursor-pointer" data-testid="merch-upload-btn">
                <Upload size={12} /> Upload image
              </label>
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.available}
                onChange={upd("available")}
                className="accent-silver-200"
                data-testid="merch-available"
              />
              <span className="font-title text-[0.6rem] tracking-[0.24em] text-silver-300 uppercase">
                Available (visible on public page)
              </span>
            </label>
          </div>
          <div className="md:col-span-3 flex gap-3 justify-end pt-4">
            <button onClick={cancel} className="btn-ghost" data-testid="merch-cancel">
              <X size={14} /> Cancel
            </button>
            <button onClick={save} className="btn-sharp" disabled={loading} data-testid="merch-save">
              <Save size={14} /> {loading ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-10" data-testid="admin-merch-list">
        {CATS.map((cat) => (
          <section key={cat}>
            <div className="font-title text-[0.72rem] tracking-[0.28em] text-silver-100 uppercase mb-3 hairline-b pb-3">
              {cat} · {(byCategory[cat] || []).length}
            </div>
            <div className="flex flex-col">
              {(byCategory[cat] || []).map((m) => (
                <div
                  key={m.id}
                  className="hairline-t py-3 grid grid-cols-12 gap-3 items-center text-sm"
                  data-testid={`admin-merch-${m.id}`}
                >
                  <div className="col-span-1 font-title text-[0.62rem] tracking-[0.2em] text-silver-500">
                    #{m.order || 0}
                  </div>
                  <div className="col-span-1">
                    {m.image_url ? (
                      <img src={m.image_url} alt="" className="h-10 w-10 object-cover border border-silver-800" />
                    ) : (
                      <div className="h-10 w-10 border border-silver-800 flex items-center justify-center text-silver-700 text-xs">—</div>
                    )}
                  </div>
                  <div className="col-span-4 text-silver-200 truncate">{m.title}</div>
                  <div className="col-span-2 text-silver-500 text-xs truncate">{m.price || "—"}</div>
                  <div className="col-span-2 text-silver-500 text-xs truncate">
                    {m.available ? (
                      <span className="text-silver-300">Visible</span>
                    ) : (
                      <span className="text-silver-700">Hidden</span>
                    )}
                  </div>
                  <div className="col-span-2 flex justify-end gap-2">
                    <button
                      onClick={() => startEdit(m)}
                      className="p-1.5 text-silver-400 hover:text-silver-100"
                      data-testid={`admin-edit-merch-${m.id}`}
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => remove(m.id)}
                      className="p-1.5 text-silver-400 hover:text-red-400"
                      data-testid={`admin-delete-merch-${m.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {(byCategory[cat] || []).length === 0 && (
                <p className="text-silver-600 text-xs py-4">No products in this category yet.</p>
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}


// ---------- VIDEOS ADMIN ----------
const EMPTY_VIDEO = {
  title: "",
  description: "",
  youtube_id: "",
  direct_url: "",
  tag: "",
  thumbnail_url: "",
  order: 0,
};

function VideosAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_VIDEO);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const r = await api.get("/videos");
    setItems(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const startEdit = (v) => {
    setEditing(v.id);
    setForm({ ...EMPTY_VIDEO, ...v });
  };
  const startNew = () => {
    setEditing("new");
    setForm(EMPTY_VIDEO);
  };
  const cancel = () => {
    setEditing(null);
    setForm(EMPTY_VIDEO);
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.title) return toast.error("Title is required");
    if (!form.youtube_id && !form.direct_url) {
      return toast.error("Provide a YouTube ID or a direct video URL");
    }
    setLoading(true);
    try {
      const payload = { ...form, order: parseInt(form.order) || 0 };
      if (editing === "new") {
        await api.post("/videos", payload);
        toast.success("Video created");
      } else {
        await api.put(`/videos/${editing}`, payload);
        toast.success("Video updated");
      }
      await load();
      cancel();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await api.delete(`/videos/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="videos-admin">
      <div className="flex justify-between items-center mb-6">
        <div className="font-title text-[0.65rem] tracking-[0.3em] text-silver-400">
          {items.length} VIDEOS
        </div>
        <button onClick={startNew} className="btn-sharp" data-testid="admin-new-video">
          <Plus size={14} /> New Video
        </button>
      </div>

      {editing && (
        <div className="hairline p-6 mb-10 grid md:grid-cols-3 gap-4" data-testid="admin-video-editor">
          <div className="md:col-span-2">
            <Field label="Title *">
              <input className="ip-input" value={form.title} onChange={upd("title")} data-testid="video-title" />
            </Field>
          </div>
          <Field label="Order">
            <input type="number" className="ip-input" value={form.order} onChange={upd("order")} data-testid="video-order" />
          </Field>
          <Field label="YouTube video ID (recommended)">
            <input className="ip-input" value={form.youtube_id} onChange={upd("youtube_id")} placeholder="e.g. HXi7XKN-d9k" data-testid="video-youtube-id" />
          </Field>
          <Field label="Tag / Genre">
            <input className="ip-input" value={form.tag} onChange={upd("tag")} placeholder="Techno / House / Live…" data-testid="video-tag" />
          </Field>
          <Field label="Direct video URL (alternative to YouTube)">
            <input className="ip-input" value={form.direct_url} onChange={upd("direct_url")} placeholder="https://… .mp4" data-testid="video-direct" />
          </Field>
          <div className="md:col-span-3">
            <Field label="Thumbnail (optional)">
              <input className="ip-input" value={form.thumbnail_url} onChange={upd("thumbnail_url")} data-testid="video-thumb-url" placeholder="Paste URL or upload below" />
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                {form.thumbnail_url && (
                  <img src={form.thumbnail_url} alt="thumb" className="h-14 w-24 object-cover border border-silver-800" onError={(e) => (e.currentTarget.style.display = "none")} />
                )}
                <UploadImageButton
                  onUploaded={(url) => setForm((f) => ({ ...f, thumbnail_url: url }))}
                  slot={`video-thumb-${editing || "new"}`}
                  accept="image"
                />
              </div>
            </Field>
          </div>
          <div className="md:col-span-3">
            <Field label="Description (optional)">
              <textarea rows={3} className="ip-input resize-y" value={form.description} onChange={upd("description")} data-testid="video-description" />
            </Field>
          </div>
          <div className="md:col-span-3 flex gap-3 justify-end pt-4">
            <button onClick={cancel} className="btn-ghost" data-testid="video-cancel">
              <X size={14} /> Cancel
            </button>
            <button onClick={save} className="btn-sharp" disabled={loading} data-testid="video-save">
              <Save size={14} /> {loading ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col" data-testid="admin-video-list">
        {items.map((v) => (
          <div
            key={v.id}
            className="hairline-t py-3 grid grid-cols-12 gap-3 items-center text-sm"
            data-testid={`admin-video-${v.id}`}
          >
            <div className="col-span-1 font-title text-[0.62rem] tracking-[0.2em] text-silver-500">
              #{v.order || 0}
            </div>
            <div className="col-span-4 text-silver-200 truncate">{v.title}</div>
            <div className="col-span-2 text-silver-500 text-xs truncate">{v.tag || "—"}</div>
            <div className="col-span-3 text-silver-500 text-xs truncate">
              {v.youtube_id ? `youtube:${v.youtube_id}` : v.direct_url ? "direct file" : "—"}
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <button onClick={() => startEdit(v)} className="p-1.5 text-silver-400 hover:text-silver-100" data-testid={`admin-edit-video-${v.id}`}>
                <Edit2 size={14} />
              </button>
              <button onClick={() => remove(v.id)} className="p-1.5 text-silver-400 hover:text-red-400" data-testid={`admin-delete-video-${v.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-silver-500 text-sm py-10 text-center">No videos yet.</p>
        )}
      </div>
    </div>
  );
}


// ---------- TECH RIDER PASSWORD CARD ----------
function TechRiderPasswordCard() {
  const [value, setValue] = useState("");
  const [draft, setDraft] = useState("");
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings/tech-rider-password");
      setValue(r.data.value || "");
    } catch {
      // Silent — endpoint may not be available
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const startEdit = () => {
    setDraft(value);
    setEditing(true);
  };
  const cancel = () => {
    setDraft("");
    setEditing(false);
  };
  const save = async () => {
    if (!draft.trim()) {
      toast.error("Password cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/settings/tech-rider-password", { value: draft.trim() });
      setValue(r.data.value);
      toast.success("Tech Rider access code updated");
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const masked = value ? "•".repeat(Math.max(4, value.length)) : "";

  return (
    <div className="hairline mb-8 p-5 flex items-center gap-6 flex-wrap" data-testid="tech-rider-pw-card">
      <div className="flex items-center gap-3 text-silver-200">
        <Lock size={18} className="text-silver-400" />
        <div>
          <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-500 uppercase">
            Tech Rider Access Code
          </div>
          <div className="font-body text-[0.7rem] text-silver-600 mt-0.5">
            Promoters use this code to unlock the Tech Rider page.
          </div>
        </div>
      </div>

      {!editing ? (
        <>
          <div className="font-title text-sm tracking-[0.18em] text-silver-100 ml-auto flex items-center gap-3" data-testid="trp-value">
            {loading ? "Loading…" : show ? value || "—" : masked || "—"}
            {value && (
              <button
                onClick={() => setShow(!show)}
                className="text-silver-500 hover:text-silver-100 transition-colors"
                aria-label={show ? "Hide" : "Show"}
                data-testid="trp-toggle-visibility"
              >
                {show ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            )}
          </div>
          <button onClick={startEdit} className="btn-ghost" data-testid="trp-edit">
            <Edit2 size={12} /> Change
          </button>
        </>
      ) : (
        <>
          <input
            type="text"
            className="ip-input ml-auto w-56"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="New access code"
            data-testid="trp-input"
          />
          <button onClick={cancel} className="btn-ghost" data-testid="trp-cancel">
            <X size={12} /> Cancel
          </button>
          <button onClick={save} className="btn-sharp" disabled={saving} data-testid="trp-save">
            <Save size={12} /> {saving ? "Saving…" : "Save"}
          </button>
        </>
      )}
    </div>
  );
}

